import React, { useEffect, useState, useRef, MouseEvent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import {
  Button,
  LinearProgress,
  Tooltip,
  TooltipProps,
  linearProgressClasses,
  tooltipClasses,
} from '@mui/material';
import { styled } from '@mui/system';
import { cloneDeep, isEmpty, round } from 'lodash';

import Color from '@tool/color';
import {
  CloseIcon20,
  UpIcon,
  DownIcon,
  UpChangeIcon,
  DownChangeIcon,
} from '@styled/icon';
import { newRate } from '@tool/index';
import TalentChart from './talent-chart';
import moUser from '@/store/user';
import moIndustry from '../../store/industry';
import HRService from '@/service/hr';
import Empty from '@/component/empty';

interface IProps {}
interface IState {}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} placement="top" />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
    padding: '10px 20px',
    '& p': {
      color: Color.white,
      display: 'flex',
      justifyContent: 'space-between',
      width: '200px',
      lineHeight: '34px',
      fontWeight: 600,
      '& label': {
        fontFamily: 'Mont SemiBold',
      },
      '& span': {
        color: Color.whiteColor(0.7),
      },
    },
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
}));

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 5,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor:
      theme.palette.mode === 'light' ? Color.secondary : Color.ec,
  },
}));

const Talent = (props: IProps, state: IState) => {
  const navi = useNavigate();
  const { talentList } = moIndustry;

  const changeUrl = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await HRService.editUserOption({
        my_industry:
          talentList?.recruit_rank?.industry_lv2 ||
          talentList?.recruit_rank?.industry_lv1,
      });
    }
    window.location.href = '/index/industry-research/0';
  };

  const goIndustry = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await HRService.editUserOption({
        my_industry: talentList?.recruit_company?.industry,
      });
    }
    window.location.href = '/index/human-demand/1?id=1';
  };
  const goCompany = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await HRService.editUserOption({
        my_industry: talentList?.recruit_company?.industry,
      });
    }
    window.location.href = '/index/human-demand/2?id=2';
  };
  const goCity = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await HRService.editUserOption({
        my_industry: talentList?.recruit_city?.industry,
      });
    }
    window.location.href = '/index/human-demand/3?id=3';
  };
  const goRole = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await HRService.editUserOption({
        my_industry: talentList?.recruit_city?.industry,
      });
    }
    window.location.href = '/index/human-demand/4?id=4';
  };
  const goRank = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await HRService.editUserOption({
        my_industry: talentList?.recruit_city?.industry,
      });
    }
    window.location.href = '/index/human-demand/2?id=2';
  };

  const getMax = useMemo(() => {
    const result: any = cloneDeep(talentList);
    const list: any = result?.recruit_top_company?.sort(
      (a: any, b: any) => b.amount - a.amount,
    );
    return !isEmpty(list) ? list[0].amount : 100;
  }, [talentList]);

  useEffect(() => {
    return () => {};
  }, []);

  return (
    <section className="dash-effect-warp">
      <section className="dash-industry-content">
        <h3 className="top">人力核心指标 - 人才需求</h3>
        <section className="contents">
          <section className="left dash-move" onClick={goIndustry}>
            <h4>招聘量热度</h4>
            <span>50中等</span>
            <TalentChart />
          </section>
          <section className="mid dash-move" onClick={goCompany}>
            <h4>
              扩招公司占比
              {/* &nbsp;<span>new</span> */}
            </h4>
            <HtmlTooltip
              title={
                <React.Fragment>
                  <p>
                    <span>行业</span>
                    <b>{talentList?.recruit_company?.industry}</b>
                  </p>
                  <p>
                    <span>时间</span>
                    <label>{talentList?.recruit_company?.year}</label>
                  </p>
                </React.Fragment>
              }>
              <b>
                {talentList?.recruit_company?.increase_ratio ?
                  `${round(
                    talentList?.recruit_company?.increase_ratio * 100,
                    1,
                  )}%` :
                  '-'}
              </b>
            </HtmlTooltip>

            <section className="change-radio">
              <a>较上期变化</a>
              {talentList?.recruit_company?.increase_ratio_change ||
              talentList?.recruit_company?.increase_ratio_change === 0 ? (
                  <span>
                    {talentList?.recruit_company?.increase_ratio_change >= 0 ? (
                      <UpChangeIcon />
                    ) : (
                      <DownChangeIcon />
                    )}
                  &nbsp;
                    <i
                      style={{
                        color:
                        talentList?.recruit_company?.increase_ratio_change >= 0 ?
                          Color.red :
                          Color.success,
                      }}>
                      {round(
                        Math.abs(
                          talentList?.recruit_company?.increase_ratio_change,
                        ) * 100,
                        1,
                      )}
                    %
                    </i>
                  </span>
                ) : (
                  '-'
                )}
            </section>
          </section>
          <section className="mid dash-move" onClick={goCity}>
            <h4>
              城市招聘趋势
              {/* &nbsp;<span>new</span> */}
            </h4>
            <HtmlTooltip
              title={
                <React.Fragment>
                  <p>
                    <span>行业</span>
                    <b>{talentList?.recruit_city?.industry}</b>
                  </p>
                  <p>
                    <span>时间</span>
                    <label>{talentList?.recruit_city?.date_tag}</label>
                  </p>
                  <p>
                    <span>地区</span>
                    <span>{talentList?.recruit_city?.city}</span>
                  </p>
                </React.Fragment>
              }>
              <b>
                {talentList?.recruit_city?.amount_current ||
                talentList?.recruit_city?.amount_current === 0 ?
                  round(
                    talentList?.recruit_city?.amount_current,
                    1,
                  ).toLocaleString() :
                  '-'}
              </b>
            </HtmlTooltip>

            <section className="change-radio">
              <a>较上期变化</a>
              {talentList?.recruit_city?.amount_change ||
              talentList?.recruit_city?.amount_change === 0 ? (
                  <span>
                    {talentList?.recruit_city?.amount_change >= 0 ? (
                      <UpChangeIcon />
                    ) : (
                      <DownChangeIcon />
                    )}
                  &nbsp;
                    <i
                      style={{
                        color:
                        talentList?.recruit_city?.amount_change >= 0 ?
                          Color.red :
                          Color.success,
                      }}>
                      {round(
                        Math.abs(talentList?.recruit_city?.amount_change) * 100,
                        1,
                      )}
                    %
                    </i>
                  </span>
                ) : (
                  '-'
                )}
            </section>
          </section>
          <section className="special dash-move" onClick={goRole}>
            <h4>
              职能招聘趋势
              {/* &nbsp;<span>new</span> */}
            </h4>
            {!isEmpty(talentList?.recruit_category) ? (
              <section className="type-items">
                {talentList?.recruit_category?.map((res: any, r: any) => {
                  return (
                    <section
                      className={
                        r === 0 && talentList?.recruit_category.length > 2 ?
                          'item' :
                          ''
                      }
                      key={r}>
                      <span>
                        <i
                          style={{
                            color:
                              res.amount_change >= 0 ?
                                Color.red :
                                Color.success,
                          }}>
                          {res.amount_change || res.amount_change === 0 ?
                            `${round(res.amount_change * 100, 1)}%` :
                            ''}
                        </i>
                        <label>{res?.category}</label>
                      </span>
                    </section>
                  );
                })}
              </section>
            ) : (
              <Empty height="auto" />
            )}
          </section>
          <section className="right dash-move common-progress" onClick={goRank}>
            <h4>
              典型公司招聘量排行
              {/* &nbsp;<span>new</span> */}
            </h4>
            {!isEmpty(talentList?.recruit_top_company) ? (
              <section  style={{ width:'100%' }}>
                {talentList?.recruit_top_company?.map((res: any, r: any) => {
                  return (
                    <section key={r} className="progress-item">
                      <b>{r + 1}</b>
                      <section className="liner-show">
                        <p title={res.company_name}>{res.company_name}</p>
                        <BorderLinearProgress
                          variant="determinate"
                          value={(res.amount / getMax) * 100}/>
                      </section>
                      <span className="numbers" style={{ minWidth:'30px' }}>
                        {res?.amount?.toLocaleString()}
                      </span>
                    </section>
                  );
                })}
              </section>
            ) : (
              <Empty height="auto" />
            )}
          </section>
        </section>
      </section>
      {!moUser.isLogin && (
        <section className="position-title">
          全行业数据 - 登录体验更细分维度数据
        </section>
      )}
      {moUser.isIndicatorExpired && (
        <section className="position-title">
          全行业数据 - 开通会员，支持所有细分行业数据
        </section>
      )}
    </section>
  );
};

export default observer(Talent);
